import React, { useState, useEffect } from 'react';
import Loader from '../../../../atoms/icons/loader';
import { Line } from 'react-chartjs-2';
import RangeSelector from '../../../../atoms/range-slider';
import { BQLink } from '../../../../atoms/bqlink';
import { useQuery } from 'react-query';
import { ArrowRightIcon, ListBoxOutlineIcon } from '../../../../atoms/material-icons';
import isEmpty from 'lodash/isEmpty';
import EmptyState from '../empty-state';
import { isWithinTradingHours, makeApiCall, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import BreakoutAlerts from '../../../breakout-alerts';
import { useInterval } from '../../../../../helpers/custom-hooks';
import './indian-indices.m.css';

const IndianIndices = ({ widget = false, container = '', budget_hide_components = false }) => {
  const [indianIndicesData, setIndianIndicesData] = useState([]);
  const [indicesDetails, setIndicesDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('1D');
  const [oneD, setOneD] = useState(true);
  const [oneW, setOneW] = useState(false);
  const [oneM, setOneM] = useState(false);
  const [oneYTD, setOneYTD] = useState(false);
  const [oneY, setOneY] = useState(false);
  const [threeY, setThreeY] = useState(false);
  const [toggle, setToggle] = useState(0);
  const [selectedKey, setSelectedKey] = useState('1');
  const [initialLoading, setInitialLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const [isDarkMode, setIsDarkMode] = useState(false);
  const widgetParam = widget ? '?view=apps' : '';
  const [indicesData, setIndicesData] = useState([]);

  const getIndianIndicesData = async (duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/indian-indices/${duration}`);
    return response.json();
  };

  const getIndicesDetailsData = async (key = '1', duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
    return response.json();
  };

  const resultIndianIndices = useQuery(
    'getIndianIndicesData',
    () => getIndianIndicesData(selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndianIndicesData(data.data);
      },
      enabled:
        (initialLoading ||
          (!!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays))) &&
        !budget_hide_components,
    },
  );

  const resultIndianIndicesDetails = useQuery(
    'getIndianIndicesDetailsData',
    () => getIndicesDetailsData(selectedKey, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndicesDetails(data.data);
      },
      enabled:
        (initialLoading ||
          (!!selectedKey &&
            !!selectedDuration &&
            isWithinTradingHours(tradingHolidays, specialTradingDays))) &&
        !budget_hide_components,
    },
  );

  useEffect(() => {
    if (initialLoading && budget_hide_components) {
      getTickerDataForBudgetDay();
    }
  }, []);

  useInterval(() => {
    if (isWithinTradingHours(tradingHolidays, specialTradingDays)) {
      if (budget_hide_components) {
        getTickerDataForBudgetDay();
      }
    }
  }, 60000);

  const getTickerDataForBudgetDay = () => {
    makeApiCall('/foapi/market/topTicker')
      .then((response) => {
        if (response.status) {
          setIndicesData(response.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.screen.width;
      setIsMobile(width <= 767);
    };

    handleResize();
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (budget_hide_components) {
      return;
    }
    const getIndianIndicesData = async () => {
      const response = await fetch('/feapi/markets/indices/indian-indices/1D');
      const data = await response.json();
      setIndianIndicesData(data.data);
    };

    const getIndicesDetailsData = async () => {
      const response = await fetch('/feapi/markets/indices/1/details/1D');
      const data = await response.json();
      setIndicesDetails(data.data);
    };

    getIndianIndicesData();
    getIndicesDetailsData();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTimeFrame = (time) => {
    setOneD(time === '1D');
    setOneW(time === '1W');
    setOneM(time === '1M');
    setOneYTD(time === 'YTD');
    setOneY(time === '1Y');
    setThreeY(time === '3Y');
    durationHandler(time);
    setSelectedDuration(time);
    indianIndicesData?.length && fetchIndiceDetailsHandler(indianIndicesData[0]?.id, time, 0);
  };

  useEffect(() => {
    if (resultIndianIndices?.isSuccess) {
      setIndianIndicesData(resultIndianIndices?.data?.data);
    }

    if (resultIndianIndicesDetails?.isSuccess) {
      setIndicesDetails(resultIndianIndicesDetails?.data?.data);
    }
  }, []);

  const durationHandler = async (duration) => {
    setLoading(true);
    const response = await fetch(`/feapi/markets/indices/indian-indices/${duration}`);
    const data = await response.json();
    setIndianIndicesData(data.data);
    setLoading(false);
  };

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
      y: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const requiredLabels =
    indicesDetails &&
    indicesDetails?.graph?.map((value) => {
      return value[0];
    });

  const dataGraph = {
    labels: requiredLabels,
    datasets: [
      {
        fill: true,
        label: '',
        data:
          indicesDetails &&
          indicesDetails?.graph?.map((value) => {
            return value[1];
          }),
        borderColor: '#14c588',
        backgroundColor: '#14c588',
      },
    ],
  };

  const fetchIndiceDetailsHandler = async (key, duration, index) => {
    try {
      setLoading(true);
      const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
      const data = await response.json();
      setIndicesDetails(data.data);
      setLoading(false);
      setToggle(index);
    } catch (error) {
      console.log('Error Fetching Indice details', error);
    }
  };

  const getStockLink = (id, name) => {
    const link = `/stock/${id}/${name
      ?.trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/\./g, '')}${widgetParam}`;

    const safeLink = widgetLink(link, widget);
    return safeLink;
  };

  const total = indicesDetails?.highlights?.declines + indicesDetails?.highlights?.advances;

  if (resultIndianIndices?.isLoading || resultIndianIndicesDetails?.isLoading) {
    <div>
      <Loader color={'#dadee1'} />
    </div>;
  }

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return indianIndicesData?.length === 0 && isEmpty(indicesDetails) && indicesData?.length === 0 ? (
    <div styleName="no-content-outermost">
      <div styleName="header-title-container">
        <ListBoxOutlineIcon size={'26px'} color={'#d01e51'} />
        <h4 styleName="list-header-title">Indices</h4>
      </div>
      <div styleName="no-data-container-ext">
        <div styleName="no-data-container-int">
          <EmptyState />
        </div>
      </div>
    </div>
  ) : (
    <div styleName={`indian-indices-container ${widget ? 'widget' : ''}`}>
      <div styleName="indian-ind-left">
        <div styleName="header-title-container">
          <ListBoxOutlineIcon size={'26px'} color={'#d01e51'} />
          <h4 styleName="list-header-title">Indices</h4>
        </div>
        {!budget_hide_components && (
          <div styleName="btn-container">
            <button
              styleName={`filter-btn ${oneD ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('1D')}
            >
              1D
            </button>
            <button
              styleName={`filter-btn ${oneW ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('1W')}
            >
              1W
            </button>
            <button
              styleName={`filter-btn ${oneM ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('1M')}
            >
              1M
            </button>
            <button
              styleName={`filter-btn ${oneYTD ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('YTD')}
            >
              YTD
            </button>
            <button
              styleName={`filter-btn ${oneY ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('1Y')}
            >
              1Y
            </button>
            <button
              styleName={`filter-btn ${threeY ? 'selected' : ' '} `}
              onClick={() => setTimeFrame('3Y')}
            >
              3Y
            </button>
          </div>
        )}
        {loading ? (
          <div styleName="loader-container">
            <Loader color={'#dadee1'} />
          </div>
        ) : (
          <div styleName="stats-outer-container">
            <React.Fragment>
              {!budget_hide_components ? (
                <React.Fragment>
                  {indianIndicesData.map((indice, index) => (
                    <div
                      styleName={`stats-container ${index === toggle ? 'selected-filter' : ''}`}
                      key={indice.id}
                      onClick={() => {
                        !isMobile && setSelectedKey(indice.id);
                        !isMobile && fetchIndiceDetailsHandler(indice.id, selectedDuration, index);
                      }}
                    >
                      <div styleName="stats-one">
                        <p styleName="value-desc">{indice.name}</p>
                        {String(indice['price-movement'].chg).includes('-') ? (
                          <span styleName="negative-value">{indice['price-movement'].chg}</span>
                        ) : (
                          <span styleName="positive-value">+{indice['price-movement'].chg}</span>
                        )}
                      </div>

                      <div styleName="stats-two">
                        <p styleName="value-stat">{indice['price-movement']['current-price']}</p>
                        {String(indice['price-movement'].chgp).includes('-') ? (
                          <span styleName="negative-value">
                            &#9660;{indice['price-movement'].chgp}%
                          </span>
                        ) : (
                          <span styleName="positive-value">
                            &#9650;+{indice['price-movement'].chgp}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {indicesData?.length > 0 && (
                    <React.Fragment>
                      {indicesData.map((indice, index) => (
                        <div styleName={`stats-container`} key={index}>
                          <div styleName="stats-one">
                            <p styleName="value-desc">{indice.name}</p>
                            {String(indice['priceMovementDto'].chg).includes('-') ? (
                              <span styleName="negative-value">
                                {indice['priceMovementDto'].chg}
                              </span>
                            ) : (
                              <span styleName="positive-value">
                                +{indice['priceMovementDto'].chg}
                              </span>
                            )}
                          </div>

                          <div styleName="stats-two">
                            <p styleName="value-stat">
                              {indice['priceMovementDto']['current_price']}
                            </p>
                            {String(indice['priceMovementDto'].chgp).includes('-') ? (
                              <span styleName="negative-value">
                                &#9660;{indice['priceMovementDto'].chgp}%
                              </span>
                            ) : (
                              <span styleName="positive-value">
                                &#9650;+{indice['priceMovementDto'].chgp}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </React.Fragment>
          </div>
        )}
        {(!widget || (widget && container === 'full')) && !budget_hide_components && (
          <div styleName="view-btn-container">
            <BQLink
              styleName="view-all-ind"
              href={widgetLink(`/markets/equities/all${widgetParam}`, widget)}
              target="_blank"
              rel="noreferrer"
            >
              View All Indices{' '}
              <ArrowRightIcon
                color={isDarkMode && widget ? '#FFFFFF' : '#2b2b2b'}
                styleName="tab-icon"
              />
            </BQLink>
          </div>
        )}
      </div>
      {loading ? (
        <div styleName="indian-ind-right loader-container">
          <Loader color={'#dadee1'} />
        </div>
      ) : (
        <React.Fragment>
          {budget_hide_components ? (
            <BreakoutAlerts
              widget={true}
              isWeb={isMobile ? false : true}
              widgetRows={5}
              customClass={'pt-10'}
            />
          ) : (
            <div styleName="indian-ind-right">
              <div styleName="overall-stats">
                <div styleName="top-stats-container-one">
                  <p styleName="top-stat-title">Top Gainer</p>
                  {indicesDetails?.highlights?.topgainer?.name ? (
                    <BQLink
                      href={getStockLink(
                        indicesDetails?.highlights?.topgainer?.sid,
                        indicesDetails?.highlights?.topgainer?.name,
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <p styleName="top-stat-name">{indicesDetails?.highlights?.topgainer?.name}</p>
                    </BQLink>
                  ) : (
                    <p styleName="top-stat-name">No Gainers</p>
                  )}

                  {indicesDetails?.highlights?.topgainer?.chgp ? (
                    <p styleName="top-stat-positive">
                      &#9650;+{indicesDetails?.highlights?.topgainer?.chgp}%
                    </p>
                  ) : (
                    <p styleName="top-stat-positive">-</p>
                  )}
                </div>
                <div styleName="top-stats-container-two">
                  <p styleName="top-stat-title">Top Loser</p>
                  {indicesDetails?.highlights?.toploser?.name ? (
                    <BQLink
                      href={getStockLink(
                        indicesDetails?.highlights?.toploser?.sid,
                        indicesDetails?.highlights?.toploser?.name,
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <p styleName="top-stat-name">{indicesDetails?.highlights?.toploser?.name}</p>
                    </BQLink>
                  ) : (
                    <p styleName="top-stat-name">No Losers</p>
                  )}
                  {indicesDetails?.highlights?.toploser?.chgp ? (
                    <p styleName="top-stat-negative">
                      &#9660;{indicesDetails?.highlights?.toploser?.chgp}%
                    </p>
                  ) : (
                    <p styleName="top-stat-negative">-</p>
                  )}
                </div>
              </div>

              <div styleName="range-slider-container">
                <RangeSelector
                  min={indicesDetails['high-low'] && indicesDetails['high-low']?.low}
                  max={indicesDetails['high-low'] && indicesDetails['high-low']?.high}
                  currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
                  label=""
                />

                <RangeSelector
                  min={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-low']}
                  max={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-high']}
                  currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
                  label=""
                  labelOne="52 Week Low"
                  labelTwo="52 Week High"
                />
              </div>
              <div styleName="graph-container">
                {indicesDetails?.graph?.length !== 0 ? (
                  <Line options={options} data={dataGraph} width={'100%'} height={'60%'} />
                ) : (
                  <div styleName="no-data-container-ext-alt">
                    <div styleName="no-data-container-int">
                      <EmptyState />
                    </div>
                  </div>
                )}
              </div>
              {indicesDetails?.highlights?.advances || indicesDetails?.highlights?.declines ? (
                <>
                  <div styleName="adv-dec-details">
                    <span>{indicesDetails?.highlights?.advances}</span>
                    <span>{indicesDetails?.highlights?.declines}</span>
                  </div>
                  <div styleName="adv-dec-container">
                    <div
                      styleName="adv-bar"
                      style={{ width: `${(indicesDetails?.highlights?.advances / total) * 100}%` }}
                    />
                  </div>
                  <div styleName="adv-dec-titles">
                    <span>Advances</span>
                    <span>Declines</span>
                  </div>
                </>
              ) : (
                <div styleName="no-advances-declines">
                  <span>Advances/Declines data not available at the moment.</span>
                </div>
              )}

              <div styleName="bottom-stats-container">
                {indicesDetails['historical-returns'] &&
                  indicesDetails['historical-returns']?.map((value, index) => (
                    <div key={index} styleName="label-stat-container">
                      <span styleName="value-label">{value.label}</span>

                      {String(value.change).includes('-') ? (
                        <span styleName="value-stat-negative">-{Math.abs(value.change)}%</span>
                      ) : (
                        <span styleName="value-stat-positive">{value.change}%</span>
                      )}
                    </div>
                  ))}
              </div>
              <div styleName="ext-btn-container">
                <BQLink
                  styleName="ext-btn"
                  href={widgetLink(`/markets/equities/stocks-in-indices${widgetParam}`, widget)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Stocks in SENSEX
                </BQLink>
                <BQLink
                  styleName="ext-btn"
                  href={widgetLink(`/markets/equities/historical-returns${widgetParam}`, widget)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Historical Returns
                </BQLink>
              </div>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default IndianIndices;
